import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { SearchParameterType, SearchParameterTypeFormData, SearchParameterTypeTestData } from '../types';
import { toast } from '../utils/toast';
import { MasterDataImportExport } from '../components/admin/MasterDataImportExport';

interface SearchParameterFormProps {
  parameter?: SearchParameterType;
  onSubmit: (data: SearchParameterTypeFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const SearchParameterForm: React.FC<SearchParameterFormProps> = ({
  parameter,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<SearchParameterTypeFormData>({
    name: parameter?.name || '',
    display_name: parameter?.display_name || '',
    description: parameter?.description || '',
    validation_regex: parameter?.validation_regex || '',
    validation_error_message: parameter?.validation_error_message || '',
    order: parameter?.order || 0,
    status: parameter?.status || 'active'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.display_name.trim()) newErrors.display_name = 'Display name is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.validation_regex.trim()) newErrors.validation_regex = 'Validation regex is required';
    if (!formData.validation_error_message.trim()) newErrors.validation_error_message = 'Validation error message is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  const handleInputChange = (field: keyof SearchParameterTypeFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
      <h3 className="text-lg font-semibold mb-4">
        {parameter ? 'Edit Search Parameter' : 'Add New Search Parameter'}
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Name (snake_case)
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., phone_number"
              disabled={isLoading}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={formData.display_name}
              onChange={(e) => handleInputChange('display_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., Phone Number"
              disabled={isLoading}
            />
            {errors.display_name && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.display_name}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Order
            </label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="0"
              min="0"
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Lower numbers appear first in the list</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value as 'active' | 'inactive')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={isLoading}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            rows={3}
            placeholder="Describe this search parameter type..."
            disabled={isLoading}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.description}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Validation Regex
          </label>
          <input
            type="text"
            value={formData.validation_regex}
            onChange={(e) => handleInputChange('validation_regex', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="e.g., ^\+?1?\d{9,15}$"
            disabled={isLoading}
          />
          {errors.validation_regex && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.validation_regex}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Validation Error Message
          </label>
          <input
            type="text"
            value={formData.validation_error_message}
            onChange={(e) => handleInputChange('validation_error_message', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="e.g., Please enter a valid phone number"
            disabled={isLoading}
          />
          {errors.validation_error_message && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.validation_error_message}</p>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : (parameter ? 'Update' : 'Create')}
          </button>
        </div>
      </form>
    </div>
  );
};

interface TestValidationProps {
  parameter: SearchParameterType;
  onClose: () => void;
}

const TestValidation: React.FC<TestValidationProps> = ({ parameter, onClose }) => {
  const [testValue, setTestValue] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleTest = async () => {
    if (!testValue.trim()) {
      toast.error('Please enter a test value');
      return;
    }

    setIsLoading(true);
    try {
      const result = await apiService.testSearchParameterValidation(parameter.id, { testValue });
      setTestResult(result);
      toast.success(`Test value is ${result.isValid ? 'valid' : 'invalid'}`);
    } catch (error) {
      toast.error('Failed to test validation');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">Test Validation</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Test Value
            </label>
            <input
              type="text"
              value={testValue}
              onChange={(e) => setTestValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter a value to test..."
            />
          </div>

          {testResult && (
            <div className={`p-3 rounded-md ${testResult.isValid ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
              <p className={`text-sm font-medium ${testResult.isValid ? 'text-green-800' : 'text-red-800'}`}>
                {testResult.isValid ? '✓ Valid' : '✗ Invalid'}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                Pattern: {testResult.regexPattern}
              </p>
              {!testResult.isValid && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  Error: {testResult.errorMessage}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200"
            >
              Close
            </button>
            <button
              onClick={handleTest}
              disabled={isLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {isLoading ? 'Testing...' : 'Test'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel'
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-6">{message}</p>
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminSearchParametersPage: React.FC = () => {
  const [parameters, setParameters] = useState<SearchParameterType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingParameter, setEditingParameter] = useState<SearchParameterType | null>(null);
  const [showTestValidation, setShowTestValidation] = useState(false);
  const [testingParameter, setTestingParameter] = useState<SearchParameterType | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [parameterToDelete, setParameterToDelete] = useState<SearchParameterType | null>(null);

  const loadParameters = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getSearchParameters();
      setParameters(data);
    } catch (error) {
      toast.error('Failed to load search parameters');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadParameters();
  }, [loadParameters]);

  const handleCreate = async (formData: SearchParameterTypeFormData) => {
    try {
      setFormLoading(true);
      await apiService.createSearchParameter(formData);
      toast.success('Search parameter created successfully');
      setShowForm(false);
      loadParameters();
    } catch (error) {
      toast.error('Failed to create search parameter');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async (formData: SearchParameterTypeFormData) => {
    if (!editingParameter) return;
    
    try {
      setFormLoading(true);
      await apiService.updateSearchParameter(editingParameter.id, formData);
      toast.success('Search parameter updated successfully');
      setShowForm(false);
      setEditingParameter(null);
      loadParameters();
    } catch (error) {
      toast.error('Failed to update search parameter');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (parameter: SearchParameterType) => {
    setParameterToDelete(parameter);
    setShowConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (!parameterToDelete) return;
    
    try {
      await apiService.deleteSearchParameter(parameterToDelete.id);
      toast.success('Search parameter deactivated successfully');
      loadParameters();
    } catch (error) {
      toast.error('Failed to deactivate search parameter');
    } finally {
      setShowConfirmDialog(false);
      setParameterToDelete(null);
    }
  };

  const handleActivate = async (parameter: SearchParameterType) => {
    try {
      await apiService.activateSearchParameter(parameter.id);
      toast.success('Search parameter activated successfully');
      loadParameters();
    } catch (error) {
      toast.error('Failed to activate search parameter');
    }
  };

  const handleDeactivate = async (parameter: SearchParameterType) => {
    try {
      await apiService.deactivateSearchParameter(parameter.id);
      toast.success('Search parameter deactivated successfully');
      loadParameters();
    } catch (error) {
      toast.error('Failed to deactivate search parameter');
    }
  };

  const handleEdit = (parameter: SearchParameterType) => {
    setEditingParameter(parameter);
    setShowForm(true);
  };

  const handleTestValidation = (parameter: SearchParameterType) => {
    setTestingParameter(parameter);
    setShowTestValidation(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingParameter(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading search parameters...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Search Parameters Management</h1>
        <div className="flex items-center gap-3">
          <MasterDataImportExport resource="search_parameters" onComplete={loadParameters} />
          <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          Add New Parameter
        </button>
        </div>
      </div>

      {showForm && (
        <div className="mb-6">
          <SearchParameterForm
            parameter={editingParameter || undefined}
            onSubmit={editingParameter ? handleUpdate : handleCreate}
            onCancel={handleCancelForm}
            isLoading={formLoading}
          />
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Search Parameters</h2>
        </div>
        
        <div className="overflow-x-auto custom-scrollbar">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Display Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {parameters.map((parameter) => (
                <tr key={parameter.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {parameter.order}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {parameter.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {parameter.display_name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    <div className="max-w-xs truncate" title={parameter.description}>
                      {parameter.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      parameter.status === 'active' 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800' 
                        : 'bg-red-100 dark:bg-red-900/30 text-red-800'
                    }`}>
                      {parameter.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(parameter)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleTestValidation(parameter)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-900"
                      >
                        Test
                      </button>
                      {parameter.status === 'active' ? (
                        <button
                          onClick={() => handleDeactivate(parameter)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivate(parameter)}
                          className="text-green-600 dark:text-green-400 hover:text-green-900"
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {parameters.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No search parameters found.
          </div>
        )}
      </div>

      {showTestValidation && testingParameter && (
        <TestValidation
          parameter={testingParameter}
          onClose={() => {
            setShowTestValidation(false);
            setTestingParameter(null);
          }}
        />
      )}

      <ConfirmationDialog
        isOpen={showConfirmDialog}
        title="Deactivate Search Parameter"
        message={`Are you sure you want to deactivate "${parameterToDelete?.display_name}"?`}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirmDialog(false);
          setParameterToDelete(null);
        }}
        confirmText="Deactivate"
        cancelText="Cancel"
      />
    </div>
  );
};

export default AdminSearchParametersPage; 